import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Checkbox, Group, Menu, NumberInput, SegmentedControl, Select, SimpleGrid, Slider, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import {
  type HoaiCalculation, type HoaiService, type HoaiZone,
  calculateHoai, calculateVariant, defaultHoaiInput, eligibleCostsBuilding, hoaiService, zoneForPoints,
} from '@pst/shared';
import { ApiError, offersApi, type Offer, type OfferVariantData } from '../../api';
import { HOAI_SERVICE_LABELS, formatEuro, formatPercent, hoaiServiceOptions } from '../../format';
import { Section } from '../../ui/Section';

const ZONES = ['I', 'II', 'III', 'IV', 'V'];

/** Buildings and interiors take their eligible costs from KG 300 and KG 400 (§ 33). */
const fromKg = (service: HoaiService): boolean => service === 'gebaeude' || service === 'innenraeume';

function newCalc(service: HoaiService): HoaiCalculation {
  return { label: HOAI_SERVICE_LABELS[service], input: defaultHoaiInput(service), building: fromKg(service) ? { kg300: 0, kg400: 0, plansKg400: false } : null, points: null };
}

/**
 * Fee after the HOAI 2021 (AP-104) beside the calculation by hours: one or
 * more calculations per offer (a building and its open spaces, say), the
 * range from Basishonorarsatz to oberer Honorarsatz, and the comparison with
 * each variant. Orientation values, not binding rates.
 */
export function OfferHoai({ offer, onSaved }: { offer: Offer; onSaved: () => Promise<void> }): JSX.Element {
  const [calcs, setCalcs] = useState<HoaiCalculation[]>(offer.hoai);
  const version = useRef(offer.version);
  const [state, setState] = useState<'saved' | 'pending' | 'saving' | 'error'>('saved');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // One save at a time, each with the version the last one returned; changes
  // made meanwhile wait and go with the next save.
  const waiting = useRef<HoaiCalculation[] | null>(null);
  const running = useRef(false);
  const save = useMutation({ mutationFn: (next: HoaiCalculation[]) => offersApi.update(offer.id, version.current, { hoai: next }) });
  const flush = async (): Promise<void> => {
    if (running.current || !waiting.current) return;
    const next = waiting.current;
    waiting.current = null;
    running.current = true;
    setState('saving');
    try {
      const r = await save.mutateAsync(next);
      version.current = r.version;
      setState(waiting.current ? 'pending' : 'saved');
    } catch {
      setState('error');
    } finally {
      running.current = false;
    }
    if (waiting.current) await flush();
    else await onSaved();
  };
  useEffect(() => { if (!running.current) version.current = offer.version; }, [offer.version]);
  // Leaving the tab with a change still waiting: save it now, not later behind the next tab's back.
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); void flush(); } }, []);
  const change = (next: HoaiCalculation[]): void => {
    setCalcs(next);
    setState('pending');
    waiting.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void flush(); }, 800);
  };
  const results = calcs.map((c) => calculateHoai(c.input));
  const sum = (k: 'net' | 'gross'): number => results.reduce((s, r) => s + r[k], 0);
  const spanAt = (rate: number): number => calcs.reduce((s, c) => s + calculateHoai({ ...c.input, rate }).net, 0);

  return (
    <Stack gap="md">
      <Alert color="gray" py={6}>
        Die Honorartafeln der HOAI 2021 sind <b>Orientierungswerte, keine verbindlichen Sätze</b>; ohne Vereinbarung in Textform gilt der Basishonorarsatz (§ 7). Die Tafeln sind aus dem amtlichen Text übernommen. Keine Rechtsberatung.
      </Alert>
      <Group gap="xs">
        <Menu withinPortal position="bottom-start">
          <Menu.Target><Button size="xs">+ Honorarermittlung</Button></Menu.Target>
          <Menu.Dropdown>
            {hoaiServiceOptions().map((g) => [
              <Menu.Label key={g.group}>{g.group}</Menu.Label>,
              ...g.items.map((s) => <Menu.Item key={s.value} onClick={() => change([...calcs, newCalc(s.value as HoaiService)])}>{s.label}</Menu.Item>),
            ])}
          </Menu.Dropdown>
        </Menu>
        <Text size="xs" c={state === 'error' ? 'red' : 'dimmed'}>{state === 'saved' ? 'gespeichert' : state === 'saving' ? 'speichert …' : state === 'pending' ? 'ungespeichert …' : 'nicht gespeichert'}</Text>
      </Group>
      {save.isError && <Alert color="red" py={6}>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      {calcs.length === 0 && <Text size="sm" c="dimmed">Noch keine Honorarermittlung.</Text>}
      {calcs.map((c, i) => (
        <CalcCard key={i} calc={c} onChange={(next) => change(calcs.map((x, j) => (j === i ? next : x)))} onRemove={() => change(calcs.filter((_, j) => j !== i))} />
      ))}
      {calcs.length > 0 && (
        <Section title="Vergleich mit der Kalkulation" description="HOAI-Honorar aller Ermittlungen zusammen gegen die Summe netto je Variante (Stundenkalkulation).">
          <Table verticalSpacing={3} withTableBorder>
            <Table.Tbody>
              <Table.Tr><Table.Td>HOAI, gewählter Satz</Table.Td><Table.Td ta="right"><Text fw={700}>{formatEuro(sum('net'))}</Text></Table.Td><Table.Td /></Table.Tr>
              <Table.Tr><Table.Td>HOAI, Basishonorarsatz … oberer Honorarsatz</Table.Td><Table.Td ta="right">{formatEuro(spanAt(0))} … {formatEuro(spanAt(100))}</Table.Td><Table.Td /></Table.Tr>
              {offer.variants.map((v) => {
                const net = calculateVariant(v.data).net;
                const diff = sum('net') ? (net / sum('net') - 1) * 100 : null;
                return (
                  <Table.Tr key={v.id}>
                    <Table.Td>Variante {v.code} · {v.name}</Table.Td>
                    <Table.Td ta="right">{formatEuro(net)}</Table.Td>
                    <Table.Td>{diff !== null && <Badge variant="light" color={Math.abs(diff) < 10 ? 'green' : 'orange'}>{formatPercent(Math.round(diff), true)} zur HOAI</Badge>}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <TakeOver offer={offer} calcs={calcs} onSaved={onSaved} />
        </Section>
      )}
    </Stack>
  );
}

function CalcCard({ calc, onChange, onRemove }: { calc: HoaiCalculation; onChange: (c: HoaiCalculation) => void; onRemove: () => void }): JSX.Element {
  const { input } = calc;
  const set = (patch: Partial<HoaiCalculation['input']>): void => onChange({ ...calc, input: { ...input, ...patch } });
  const r = calculateHoai(input);
  const def = hoaiService(input.service);
  const std = def.phases.map((ph) => ph.share);
  const eligible = calc.building ? eligibleCostsBuilding(calc.building.kg300, calc.building.kg400, calc.building.plansKg400) : null;
  const setBuilding = (patch: Partial<NonNullable<HoaiCalculation['building']>>): void => {
    const b = { ...calc.building!, ...patch };
    onChange({ ...calc, building: b, input: { ...input, costs: eligibleCostsBuilding(b.kg300, b.kg400, b.plansKg400).eligible } });
  };
  const criteria = def.criteria;
  const setPoints = (points: number[] | null): void => onChange({ ...calc, points, input: { ...input, zone: points ? zoneForPoints(input.service, points.reduce((a, b) => a + b, 0)) : input.zone } });
  return (
    <Section
      title={calc.label || HOAI_SERVICE_LABELS[input.service]}
      description={`${def.name} · Honorartafel ${def.paragraphs.fees} HOAI`}
      actions={<ActionIcon variant="subtle" color="red" onClick={() => { if (window.confirm(`„${calc.label}“ entfernen?`)) onRemove(); }} aria-label="Entfernen" title="Entfernen">✕</ActionIcon>}
    >
      <Stack gap="sm">
        <TextInput size="xs" label="Bezeichnung" w={320} value={calc.label} onChange={(e) => onChange({ ...calc, label: e.currentTarget.value })} />
        {calc.building ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <NumberInput size="xs" label="Kosten KG 300 (€ netto)" value={calc.building.kg300} min={0} thousandSeparator="." decimalSeparator="," onChange={(v) => setBuilding({ kg300: Number(v) || 0 })} w={190} />
            <NumberInput size="xs" label="Kosten KG 400 (€ netto)" value={calc.building.kg400} min={0} thousandSeparator="." decimalSeparator="," onChange={(v) => setBuilding({ kg400: Number(v) || 0 })} w={190} />
            <Checkbox size="xs" label="KG 400 plant oder überwacht das Büro selbst" checked={calc.building.plansKg400} onChange={(e) => setBuilding({ plansKg400: e.currentTarget.checked })} />
            <Text size="xs" c="dimmed" pb={4}>anrechenbar nach § 33: {formatEuro(eligible!.eligible)} (davon KG 400: {formatEuro(eligible!.kg400Eligible)})</Text>
          </Group>
        ) : (
          <NumberInput size="xs" label={`${def.basisLabel}${def.basis === 'costs' ? ', netto' : ''}`} value={input.costs} min={0}
            thousandSeparator="." decimalSeparator="," decimalScale={def.basis === 'hectare' ? 2 : 0} onChange={(v) => set({ costs: Number(v) || 0 })} w={300} />
        )}
        {def.costRules && <Text size="xs" c="dimmed">{def.costRules}</Text>}
        <Group gap="md" align="flex-end" wrap="wrap">
          <Stack gap={2}>
            <Text size="xs" fw={500}>Honorarzone</Text>
            <SegmentedControl size="xs" value={String(input.zone)} onChange={(v) => { onChange({ ...calc, points: null, input: { ...input, zone: Number(v) as HoaiZone } }); }} data={ZONES.slice(0, def.zones).map((z, i) => ({ value: String(i + 1), label: z }))} />
          </Stack>
          {def.bounds && <Checkbox size="xs" label="nach Bewertungspunkten" checked={calc.points !== null} onChange={(e) => setPoints(e.currentTarget.checked ? criteria.map(() => 0) : null)} />}
          <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
            <Text size="xs" fw={500}>Honorarsatz: {input.rate === 0 ? 'Basishonorarsatz' : input.rate === 100 ? 'oberer Honorarsatz' : `${input.rate} % zwischen Basis und oberem Satz`}</Text>
            <Slider size="sm" value={input.rate} onChange={(v) => set({ rate: v })} min={0} max={100} step={5} marks={[{ value: 0, label: 'Basis' }, { value: 50, label: 'Mitte' }, { value: 100, label: 'oben' }]} mb="md" />
          </Stack>
        </Group>
        {!def.bounds && criteria.length > 0 && <Text size="xs" c="dimmed">Bewertungsmerkmale ({def.paragraphs.fees}): {criteria.map((k) => k.label).join(' · ')}</Text>}
        {calc.points && def.bounds && (
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {criteria.map((k, j) => (
              <NumberInput key={k.label} size="xs" label={k.label} description={`0 bis ${k.max ?? 0} Punkte`} value={calc.points![j] ?? 0} min={0} max={k.max ?? 0}
                onChange={(v) => setPoints(calc.points!.map((p, n) => (n === j ? Math.min(k.max ?? 0, Math.max(0, Number(v) || 0)) : p)))} />
            ))}
            <Text size="sm" pt="lg">Summe {calc.points.reduce((a, b) => a + b, 0)} Punkte → Zone {ZONES[input.zone - 1]}</Text>
          </SimpleGrid>
        )}
        <Table verticalSpacing={2} className="pst-tabular">
          <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th>Leistungsphase</Table.Th><Table.Th w={90} ta="right">HOAI %</Table.Th><Table.Th w={110} ta="right">vereinbart %</Table.Th><Table.Th w={130} ta="right">Honorar</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {std.map((s, n) => {
              const on = input.phases[n] !== null;
              const fee = r.phases.find((p) => p.lp === n + 1)?.fee;
              return (
                <Table.Tr key={n} style={on ? undefined : { opacity: 0.5 }}>
                  <Table.Td><Checkbox size="xs" checked={on} aria-label={`LP ${n + 1} beauftragt`} onChange={(e) => set({ phases: input.phases.map((p, j) => (j === n ? (e.currentTarget.checked ? s : null) : p)) })} /></Table.Td>
                  <Table.Td><Text size="sm">LP {n + 1} {def.phases[n]!.name}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{s}</Text></Table.Td>
                  <Table.Td ta="right">{on && <NumberInput size="xs" value={input.phases[n] ?? 0} min={0} max={100} step={0.5} decimalScale={2} decimalSeparator="," onChange={(v) => set({ phases: input.phases.map((p, j) => (j === n ? Number(v) || 0 : p)) })} />}</Table.Td>
                  <Table.Td ta="right"><Text size="sm">{fee !== undefined ? formatEuro(fee) : ''}</Text></Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        <Group gap="sm" align="flex-end" wrap="wrap">
          {def.maxConversion !== null && <NumberInput size="xs" label="Umbau-/Modernisierungszuschlag %" description={`HOAI: bis ${def.maxConversion} % bei durchschnittlicher Schwierigkeit${def.conversionDefault !== null ? `; ohne Vereinbarung ${def.conversionDefault} %` : ''}`} value={input.conversion} min={0} max={100} onChange={(v) => set({ conversion: Number(v) || 0 })} w={260} />}
          {def.maintenanceLp8 && <NumberInput size="xs" label="Instandsetzung: LP 8 erhöht um %" description="§ 12 Abs. 2, bis 50 %" value={input.maintenanceLp8} min={0} max={50} onChange={(v) => set({ maintenanceLp8: Number(v) || 0 })} w={200} />}
          <NumberInput size="xs" label="Nebenkosten pauschal %" description="§ 14, nur wenn vereinbart" value={input.incidentals} min={0} max={100} step={0.5} decimalScale={2} decimalSeparator="," onChange={(v) => set({ incidentals: Number(v) || 0 })} w={180} />
          <Select size="xs" label="Umsatzsteuer" w={120} value={String(input.vat)} onChange={(v) => v && set({ vat: Number(v) })} data={[{ value: '0.19', label: '19 %' }, { value: '0.07', label: '7 %' }, { value: '0', label: 'keine' }]} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        </Group>
        {r.span && (
          <Text size="sm">
            Honorar für 100 %: {formatEuro(r.span.low)} (Basis) … {formatEuro(r.span.high)} (oben), gewählt {formatEuro(r.full ?? 0)} · beauftragte Phasen {formatEuro(r.basic)}
            {r.conversion ? ` · Umbauzuschlag ${formatEuro(r.conversion)}` : ''}{r.maintenance ? ` · Instandsetzung ${formatEuro(r.maintenance)}` : ''}{r.incidentals ? ` · Nebenkosten ${formatEuro(r.incidentals)}` : ''}
            {' · '}<b>netto {formatEuro(r.net)}</b>, brutto {formatEuro(r.gross)}
          </Text>
        )}
        {r.notes.map((n) => <Alert key={n} color="orange" py={4}><Text size="xs">{n}</Text></Alert>)}
      </Stack>
    </Section>
  );
}

/** The phases as positions of a variant: hours = fee / rate of one role, marked as converted. */
function TakeOver({ offer, calcs, onSaved }: { offer: Offer; calcs: HoaiCalculation[]; onSaved: () => Promise<void> }): JSX.Element {
  const [variantId, setVariantId] = useState(offer.variants[0]!.id);
  const variant = offer.variants.find((v) => v.id === variantId) ?? offer.variants[0]!;
  const [role, setRole] = useState<string>(variant.data.roles[0]?.short ?? '');
  const r = variant.data.roles.findIndex((x) => x.short === role);
  const rate = r >= 0 ? variant.data.roles[r]!.rate ?? 0 : 0;
  const take = useMutation({
    mutationFn: () => {
      const d: OfferVariantData = structuredClone(variant.data);
      for (const c of calcs) {
        const res = calculateHoai(c.input);
        // Supplements and incidental costs go with the phases in proportion, so the variant carries the whole HOAI net.
        const factor = res.basic > 0 ? res.net / res.basic : 1;
        for (const p of res.phases) {
          const fee = Math.round(p.fee * factor * 100) / 100;
          const hours = Math.round((fee / rate) * 10) / 10;
          d.positions.push({
            title: `${c.label}: LP ${p.lp} ${hoaiService(c.input.service).phases[p.lp - 1]?.name ?? ''}`,
            hours: d.roles.map((_, j) => (j === r ? hours : null)), shares: d.subcontractors.map(() => ({ text: '', hours: null, fixed: null })),
            kind: 'Pauschale', reasoning: `[A] aus dem HOAI-Honorar umgerechnet: ${formatPercent(p.share)} von ${formatEuro(res.full ?? 0)} = ${formatEuro(p.fee)}${factor !== 1 ? `, mit Zuschlägen und Nebenkosten ${formatEuro(fee)}` : ''} ÷ ${formatEuro(rate)}/h`,
            owed: `Grundleistungen der Leistungsphase ${p.lp} nach HOAI`, excluded: '', cuts: [true, true, true],
          });
        }
      }
      return offersApi.saveVariant(offer.id, variant.id, variant.version, { data: d });
    },
    onSuccess: onSaved,
  });
  const count = calcs.reduce((s, c) => s + calculateHoai(c.input).phases.length, 0);
  return (
    <Stack gap={6} mt="sm">
      <Text size="sm" fw={500}>Als Positionen in eine Variante übernehmen</Text>
      <Group gap="sm" align="flex-end" wrap="wrap">
        <Select size="xs" label="Variante" w={240} value={variant.id} onChange={(v) => v && setVariantId(v)} data={offer.variants.map((v) => ({ value: v.id, label: `${v.code} · ${v.name}` }))} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        <Select size="xs" label="Stunden umgerechnet mit dem Satz von" w={240} value={role} onChange={(v) => v && setRole(v)} data={variant.data.roles.map((x) => ({ value: x.short, label: `${x.short} ${x.name} (${x.rate ?? '?'} €/h)` }))} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        <Button size="xs" variant="light" disabled={!rate || count === 0 || variant.data.positions.length + count > 20} loading={take.isPending}
          onClick={() => { if (window.confirm(`${count} Positionen an Variante ${variant.code} anhängen? Die Stunden sind aus dem Honorar umgerechnet und als Annahme [A] gekennzeichnet.`)) take.mutate(); }}>
          {count} Positionen anhängen
        </Button>
      </Group>
      {!rate && <Text size="xs" c="orange.8">Die gewählte Rolle hat noch keinen Stundensatz.</Text>}
      {variant.data.positions.length + count > 20 && <Text size="xs" c="orange.8">Die Vorlage hat Platz für 20 Positionen – zu viele für diese Variante.</Text>}
      {take.isError && <Alert color="red" py={6}>{take.error instanceof ApiError ? take.error.message : 'Übernehmen fehlgeschlagen.'}</Alert>}
    </Stack>
  );
}
